import React from 'react';
import { errorMessage, getRedirectStats } from '../../api';
import { useAsyncData } from '../../hooks/useAsyncData';
import type { Project, RedirectStats } from '../../types';
import { Button, Card, CardBody, InlineAlert, useToast } from '../../ui';

/** The survey tool swaps [ID] for the respondent's GUID. */
export const withRespondentId = (url: string) => (url ? `${url}${url.includes('?') ? '&' : '?'}id=[ID]` : '');

const LINKS = [
  { key: 'complete', label: 'Complete', hint: 'Respondent finished the survey' },
  { key: 'screenout', label: 'Screen-out', hint: 'Respondent did not qualify' },
  { key: 'quotaFull', label: 'Quota full', hint: 'Respondent qualified but the quota was already filled' },
] as const;

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    return false; // clipboard blocked: the link is still shown for manual copying
  }
}

/**
 * The survey redirect links to paste into the survey tool, plus how many respondents came back by each one.
 * Every link ends with `&id=[ID]`: the survey tool replaces `[ID]` with the respondent ID it was given (the GUID the
 * entry link created), which lets the server set that respondent's status and count each respondent once.
 * The server generates the links; older campaigns get theirs the next time they are saved.
 */
export function RedirectLinksCard({ project }: { project: Project }) {
  const { notify } = useToast();
  const links = project.redirectLinks;
  const hasLinks = Boolean(links && (links.complete || links.screenout || links.quotaFull));
  const stats = useAsyncData<RedirectStats>((signal) => getRedirectStats(project.id, { signal }), [project.id], { enabled: hasLinks });

  const copy = async (label: string, url: string) => {
    notify((await copyText(url)) ? `${label} link copied` : 'Could not copy. Select the link and copy it manually.', { tone: 'ok' });
  };

  return (
    <Card>
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-ink">Survey redirect links</h2>
            <p className="mt-1 text-sm text-muted">Paste these into your survey tool so respondents come back with the right outcome.</p>
          </div>
          {hasLinks && <Button variant="ghost" size="sm" onClick={stats.reload} loading={stats.loading}>Refresh counts</Button>}
        </div>

        {!hasLinks ? (
          <div className="mt-4"><InlineAlert tone="info">This campaign has no survey links yet. Older campaigns get their links the next time they are saved: open “Edit details” and save.</InlineAlert></div>
        ) : (
          <>
            <ul className="mt-4 divide-y divide-line/70">
              {LINKS.map(({ key, label, hint }) => {
                const url = withRespondentId((links && links[key]) || '');
                const count = stats.data ? stats.data[key] : null;
                return (
                  <li key={key} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 py-2.5">
                    <div className="w-40 flex-none">
                      <p className="text-sm font-semibold text-ink">{label}</p>
                      <p className="text-xs text-muted">{hint}</p>
                    </div>
                    <input readOnly aria-label={`${label} link`} value={url} onFocus={(e) => e.currentTarget.select()} className="min-w-0 flex-1 rounded-md border border-line bg-canvas px-2.5 py-1.5 font-mono text-xs text-ink" />
                    <span className="w-16 flex-none text-right text-sm font-semibold tabular-nums text-ink" aria-label={`${label} count`}>{count === null ? '—' : count.toLocaleString()}</span>
                    <Button variant="secondary" size="sm" onClick={() => copy(label, url)} disabled={!url}>Copy</Button>
                  </li>
                );
              })}
            </ul>
            <p className="mt-2 text-xs text-muted">
              The survey tool must replace <code className="rounded bg-chip px-1">[ID]</code> with the respondent ID it received, so each respondent's status is set and counted once.
              {stats.data && <> Total returned: <b className="text-ink">{stats.data.total.toLocaleString()}</b>.</>}
            </p>
            {stats.error ? <div className="mt-2"><InlineAlert tone="warn">Could not load the counts: {errorMessage(stats.error)}</InlineAlert></div> : null}
          </>
        )}
      </CardBody>
    </Card>
  );
}
